import type { EnvironmentId, PullRequestRef } from "@cmmd-forge/contracts";
import { SendIcon } from "lucide-react";
import { useState } from "react";

import { Button } from "~/components/ui/button";
import { Textarea } from "~/components/ui/textarea";
import { cmmdApiClient } from "~/environments/cmmdApi";
import { commandPullRequestRef } from "~/lib/pullRequestReactQuery";
import { usePullRequestActivityCommand } from "../timeline/usePullRequestActivityCommand";

export function PullRequestCommentComposer({
  environmentId,
  reference,
}: {
  readonly environmentId: EnvironmentId;
  readonly reference: PullRequestRef;
}) {
  const [body, setBody] = useState("");
  const command = usePullRequestActivityCommand(environmentId, reference);
  const submit = () => {
    const trimmed = body.trim();
    if (trimmed.length === 0 || command.isPending) return;
    command.mutate(async () => {
      await cmmdApiClient.createPullRequestComment({
        ...commandPullRequestRef(reference),
        idempotencyKey: crypto.randomUUID(),
        body: trimmed,
      });
      setBody("");
    });
  };
  return (
    <div className="mt-3 space-y-2">
      <Textarea
        disabled={command.isPending}
        value={body}
        rows={3}
        placeholder="Leave a comment"
        aria-label="Comment on this pull request"
        maxLength={65_536}
        onChange={(event) => setBody(event.target.value)}
      />
      {command.error !== null ? (
        <p role="alert" className="text-xs text-destructive">
          Could not post the comment. Your draft is still here.
        </p>
      ) : null}
      <div className="flex justify-end">
        <Button
          type="button"
          size="xs"
          variant="outline"
          disabled={body.trim().length === 0 || command.isPending}
          onClick={submit}
        >
          <SendIcon className="size-3.5" />
          {command.isPending ? "Posting..." : "Comment"}
        </Button>
      </div>
    </div>
  );
}
